<template>
  <div class="gate-scanner-wrap" :class="{ 'fullscreen-mode': isFullscreen }">

    <!-- Fullscreen header (only in fullscreen mode) -->
    <div v-if="isFullscreen" class="fs-header">
      <button class="btn btn-outline-light btn-sm" @click="toggleFullscreen">
        <i class="fas fa-compress-alt mr-1"></i>Exit Fullscreen
      </button>
    </div>

    <!-- Event Selector -->
    <div v-show="!isFullscreen" class="gs-card mb-3">
      <div class="gs-card-header">
        <i class="fas fa-calendar-days mr-2"></i>Select Event
      </div>
      <div class="gs-card-body">
        <div class="row align-items-end">
          <div class="col-md-8 mb-2 mb-md-0">
            <label class="gs-label">Event</label>
            <select class="form-control form-control-sm" v-model="selectedEventId" @change="onEventChange">
              <option :value="null" disabled>-- Choose an event --</option>
              <option v-for="e in events" :key="e.eventId" :value="e.eventId">
                {{ e.title }} ({{ e.eventDate }})
              </option>
            </select>
          </div>
          <div class="col-md-4">
            <button class="btn btn-outline-primary btn-sm btn-block"
                    :disabled="!selectedEventId || loadingAttendees"
                    @click="refreshAttendees">
              <i class="fas fa-sync-alt mr-1" :class="{ 'fa-spin': loadingAttendees }"></i>
              {{ loadingAttendees ? 'Loading…' : 'Refresh Attendees' }}
            </button>
          </div>
        </div>
        <div v-if="selectedEventId && !loadingAttendees" class="mt-2" style="font-size:0.8rem; color:#666;">
          <i class="fas fa-users mr-1"></i>{{ stats.total }} registered &nbsp;|&nbsp;
          <span class="text-success font-weight-bold">{{ stats.admittedCount }} admitted</span>
          <span v-if="pendingSyncCount > 0" class="ml-2 text-warning font-weight-bold">
            <i class="fas fa-bolt mr-1"></i>{{ pendingSyncCount }} pending sync
          </span>
        </div>
      </div>
    </div>

    <!-- Scanner -->
    <div class="gs-card mb-3" v-if="selectedEventId" :class="{ 'fullscreen-card': isFullscreen }">
      <div class="gs-card-header">
        <i class="fas fa-qrcode mr-2"></i>QR Code Scanner
      </div>
      <div class="gs-card-body">

        <!-- Camera controls -->
        <div class="d-flex mb-3" style="gap:8px; flex-wrap:wrap;">
          <button class="btn btn-success btn-sm"
                  @click="startCamera"
                  :disabled="cameraActive || !selectedEventId">
            <i class="fas fa-camera mr-1"></i>Start Camera
          </button>
          <button class="btn btn-secondary btn-sm"
                  @click="stopCamera"
                  :disabled="!cameraActive">
            <i class="fas fa-stop mr-1"></i>Stop
          </button>
          <button class="btn btn-outline-primary btn-sm ml-auto"
                  @click="toggleFullscreen"
                  v-show="!isFullscreen">
            <i class="fas fa-expand mr-1"></i>Fullscreen
          </button>
        </div>

        <!-- QR viewfinder -->
        <div :class="{ 'camera-container': true, 'active': cameraActive, 'fullscreen-camera': isFullscreen }">
          <div id="qr-reader" style="width:100%; height:100%;"></div>
        </div>
        <p v-if="!cameraActive && !scanResult"
           class="text-muted text-center py-3"
           style="font-size:0.85rem;">
          <i class="fas fa-qrcode fa-2x d-block mb-2 text-secondary"></i>
          Press "Start Camera" to begin scanning
        </p>

        <!-- Scan result card -->
        <div v-if="scanResult" class="mt-3 gs-result-card"
             :class="{
               'gs-result-admitted': scanResult.status === 'admitted',
               'gs-result-duplicate': scanResult.status === 'duplicate',
               'gs-result-unknown': scanResult.status === 'unknown'
             }">

          <!-- Admitted -->
          <template v-if="scanResult.status === 'admitted'">
            <div class="gs-result-icon">
              <i class="fas fa-check-circle"></i>
            </div>
            <div class="gs-result-body">
              <div class="gs-result-title">✅ Admitted</div>
              <div class="gs-result-name">{{ scanResult.firstName }} {{ scanResult.lastName }}</div>
              <div class="gs-result-meta">
                <span>{{ scanResult.membershipNo }}</span>
                <span v-if="scanResult.companyName" class="ml-2">· {{ scanResult.companyName }}</span>
              </div>
              <div class="gs-result-ticket">Ticket: {{ scanResult.acknowledgementNumber }}</div>
            </div>
          </template>

          <!-- Already checked in -->
          <template v-if="scanResult.status === 'duplicate'">
            <div class="gs-result-icon">
              <i class="fas fa-exclamation-triangle"></i>
            </div>
            <div class="gs-result-body">
              <div class="gs-result-title">⚠️ Already Checked In</div>
              <div class="gs-result-name">{{ scanResult.firstName }} {{ scanResult.lastName }}</div>
              <div class="gs-result-meta">{{ scanResult.membershipNo }}</div>
              <div class="gs-result-ticket">Ticket: {{ scanResult.acknowledgementNumber }}</div>
            </div>
          </template>

          <!-- Unknown -->
          <template v-if="scanResult.status === 'unknown'">
            <div class="gs-result-icon">
              <i class="fas fa-times-circle"></i>
            </div>
            <div class="gs-result-body">
              <div class="gs-result-title">❌ Not Registered</div>
              <div class="gs-result-meta" style="word-break:break-all;">{{ scanResult.rawText }}</div>
            </div>
          </template>

          <div v-if="scanResult.status === 'admitted'" class="mt-3 text-right">
            <button class="btn btn-sm btn-primary" @click="clearScanResult">
              <i class="fas fa-arrow-right mr-1"></i>Check In Next
            </button>
          </div>

        </div>

      </div>
    </div>

    <!-- Manual Lookup -->
    <div v-show="!isFullscreen" class="gs-card mb-3" v-if="selectedEventId">
      <div class="gs-card-header">
        <i class="fas fa-keyboard mr-2"></i>Manual Lookup (Ticket ID)
      </div>
      <div class="gs-card-body">
        <div class="input-group input-group-sm">
          <input type="text"
                 class="form-control"
                 placeholder="Enter Acknowledgement Number"
                 v-model="manualInput"
                 @keyup.enter="manualLookup" />
          <div class="input-group-append">
            <button class="btn btn-primary" @click="manualLookup"
                    :disabled="!manualInput.trim()">
              <i class="fas fa-search mr-1"></i>Check
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";

export default {
  name: "GateScanner",
  data() {
    return {
      events: [],
      selectedEventId: null,
      loadingAttendees: false,
      isFullscreen: false,

      // keyed by acknowledgementNumber.toUpperCase()
      attendeeMap: new Map(),

      // queued API calls for offline-resilient sync
      pendingQueue: [],
      syncTimer: null,
      syncBusy: false,

      cameraActive: false,
      scanPaused: false,
      html5Qrcode: null,

      scanResult: null,
      manualInput: "",

      stats: { total: 0, admittedCount: 0 },
    };
  },

  computed: {
    pendingSyncCount() {
      return this.pendingQueue.length;
    },
  },

  mounted() {
    this.loadEvents();
    this.syncTimer = setInterval(this.processSyncQueue, 3000);
  },

  beforeUnmount() {
    this.stopCamera();
    document.body.style.overflow = "auto";
    if (this.syncTimer) {
      clearInterval(this.syncTimer);
      this.syncTimer = null;
    }
  },

  methods: {
    // ─── Events ───────────────────────────────────────────────────────────────
    loadEvents() {
      axios
        .post("/api/Event/GetEventsList", {})
        .then((res) => {
          this.events = (res.data.eventCollectionItems || []).map((e) => ({
            ...e,
            eventDate: moment(e.eventDate).format("DD-MMM-YYYY"),
          }));
        })
        .catch((err) => console.error("GateScanner: loadEvents failed", err));
    },

    onEventChange() {
      this.scanResult = null;
      this.attendeeMap = new Map();
      this.stats = { total: 0, admittedCount: 0 };
      this.pendingQueue = [];
      if (this.selectedEventId) this.refreshAttendees();
    },

    // ─── Attendees ────────────────────────────────────────────────────────────
    refreshAttendees() {
      if (!this.selectedEventId) return;
      this.loadingAttendees = true;

      axios
        .post("/api/EventRegistration/GetEventRegistrationsList", {
          eventId: this.selectedEventId,
        })
        .then((res) => {
          const list = res.data || [];
          const newMap = new Map();
          let admittedCount = 0;

          list.forEach((reg) => {
            const key = (reg.acknowledgementNumber || "").toUpperCase();
            if (key) newMap.set(key, reg);
            if (reg.attended === "Y") admittedCount++;
          });

          this.attendeeMap = newMap;
          this.stats = { total: list.length, admittedCount };
        })
        .catch((err) => console.error("GateScanner: refreshAttendees failed", err))
        .finally(() => { this.loadingAttendees = false; });
    },

    // ─── Camera ───────────────────────────────────────────────────────────────
    startCamera() {
      if (this.cameraActive) return;

      // Ensure element exists and is visible
      const qrReaderEl = document.getElementById("qr-reader");
      if (!qrReaderEl) {
        alert("Camera container not found. Please try again.");
        return;
      }

      this.html5Qrcode = new Html5Qrcode("qr-reader", {
        formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
      });
      this.html5Qrcode
        .start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 260, height: 260 } },
          (decodedText) => this.onScanSuccess(decodedText),
          (err) => {
            // Log decode failures for debugging without interrupting scanning
            console.debug("GateScanner decode error", err);
          }
        )
        .then(() => { 
          this.cameraActive = true;
          console.log("Camera started successfully");
        })
        .catch((err) => {
          console.error("GateScanner: camera start failed", err);
          alert("Could not access camera. Please check:\n- Camera permissions are enabled\n- Use HTTPS or localhost\n- Or use manual ticket ID lookup");
          this.html5Qrcode = null;
        });
    },

    stopCamera() {
      if (!this.cameraActive || !this.html5Qrcode) return;
      this.html5Qrcode
        .stop()
        .then(() => {
          this.cameraActive = false;
          this.html5Qrcode.clear();
          this.html5Qrcode = null;
        })
        .catch((err) => console.error("GateScanner: camera stop failed", err));
    },

    // ─── Scan handler ─────────────────────────────────────────────────────────
    onScanSuccess(decodedText) {
      // Prevent duplicate processing while result card is showing
      if (this.scanPaused) return;
      this.scanPaused = true;
      this.processEntry(decodedText.trim());
      // Auto-resume after 2.5 s
      setTimeout(() => { this.scanPaused = false; }, 2500);
    },

    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
      if (this.isFullscreen) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "auto";
      }
    },

    processEntry(ackNo) {
      const key = ackNo.toUpperCase();
      const reg = this.attendeeMap.get(key);

      if (!reg) {
        this.scanResult = { status: "unknown", rawText: ackNo };
        return;
      }

      if (reg.attended === "Y") {
        this.scanResult = { status: "duplicate", ...reg };
        return;
      }

      // Mark locally (instant, no network wait)
      reg.attended = "Y";
      this.stats.admittedCount++;
      this.scanResult = { status: "admitted", ...reg };

      // Haptic feedback on supported devices
      if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
        navigator.vibrate([100, 50, 100]);
      }

      // Sound feedback on supported browsers
      this.playSuccessSound();

      // Queue API sync
      this.pendingQueue.push({ ...reg });
    },

    clearScanResult() {
      this.scanResult = null;
      this.scanPaused = false;
    },

    playSuccessSound() {
      if (typeof window === "undefined") return;
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;

      try {
        const audioCtx = new AudioContext();
        const oscillator = audioCtx.createOscillator();
        const gainNode = audioCtx.createGain();

        oscillator.type = "sine";
        oscillator.frequency.setValueAtTime(880, audioCtx.currentTime);
        gainNode.gain.setValueAtTime(0.001, audioCtx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.25, audioCtx.currentTime + 0.02);
        gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.2);

        oscillator.connect(gainNode);
        gainNode.connect(audioCtx.destination);

        oscillator.start();
        oscillator.stop(audioCtx.currentTime + 0.22);

        oscillator.onended = () => {
          audioCtx.close();
        };
      } catch (error) {
        console.debug("GateScanner: success sound failed", error);
      }
    },

    manualLookup() {
      const v = this.manualInput.trim();
      if (!v) return;
      this.processEntry(v);
      this.manualInput = "";
    },

    // ─── Background sync ──────────────────────────────────────────────────────
    async processSyncQueue() {
      if (this.syncBusy || this.pendingQueue.length === 0) return;
      this.syncBusy = true;

      // Process a snapshot so new items added mid-loop are not skipped
      const batch = [...this.pendingQueue];
      const toRemove = [];

      for (const item of batch) {
        try {
          await axios.post("/api/EventRegistration/MarkAttendence", item);
          toRemove.push(item);
        } catch (_) {
          // Leave in queue; will retry in next interval
        }
      }

      if (toRemove.length > 0) {
        this.pendingQueue = this.pendingQueue.filter((q) => !toRemove.includes(q));
      }

      this.syncBusy = false;
    },
  },
};
</script>

<style scoped>
/* ─── Wrapper ─────────────────────────────────────────── */
.gate-scanner-wrap {
  max-width: 640px;
  margin: 0 auto;
  padding: 6px 0;
}

.gate-scanner-wrap.fullscreen-mode {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100vh;
  max-width: 100%;
  margin: 0;
  padding: 0;
  z-index: 9999;
  background: #fff;
  display: flex;
  flex-direction: column;
}

.fs-header {
  background: linear-gradient(90deg, #3b5aa7 0%, #c0102a 100%);
  padding: 8px 12px;
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
}

/* ─── Camera container ────────────────────────────────── */
.camera-container {
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  min-height: 200px;
  border-radius: 10px;
  overflow: hidden;
  border: 2px solid #3b5aa7;
  background: #000;
}

.camera-container.fullscreen-camera {
  position: absolute;
  top: 50px;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  aspect-ratio: auto;
  min-height: auto;
  border-radius: 0;
  border: none;
  margin: 0;
}

/* ─── Card ────────────────────────────────────────────── */
.gs-card {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  overflow: hidden;
}

.gs-card.fullscreen-card {
  position: absolute;
  top: 50px;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  border-radius: 0;
  margin: 0;
  padding: 0 !important;
  display: flex;
  flex-direction: column;
  box-shadow: none;
  overflow: auto;
}
.gs-card-header {
  background: linear-gradient(90deg, #3b5aa7 0%, #c0102a 100%);
  color: #fff;
  padding: 10px 16px;
  font-size: 0.88rem;
  font-weight: 700;
  letter-spacing: 0.3px;
}
.gs-card-body {
  padding: 16px;
}
.gs-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #555;
  margin-bottom: 4px;
  display: block;
}

/* ─── Result cards ────────────────────────────────────── */
.gs-result-card {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  border-radius: 10px;
  padding: 16px;
}
.gs-result-admitted  { background: #d4edda; border: 1.5px solid #28a745; }
.gs-result-duplicate { background: #fff3cd; border: 1.5px solid #ffc107; }
.gs-result-unknown   { background: #f8d7da; border: 1.5px solid #dc3545; }

.gs-result-icon {
  font-size: 2rem;
  line-height: 1;
  flex-shrink: 0;
}
.gs-result-admitted  .gs-result-icon { color: #28a745; }
.gs-result-duplicate .gs-result-icon { color: #856404; }
.gs-result-unknown   .gs-result-icon { color: #dc3545; }

.gs-result-title {
  font-weight: 700;
  font-size: 1rem;
  margin-bottom: 4px;
}
.gs-result-name {
  font-size: 1.05rem;
  font-weight: 600;
  margin-bottom: 2px;
}
.gs-result-meta {
  font-size: 0.82rem;
  color: #555;
  margin-bottom: 2px;
}
.gs-result-ticket {
  font-size: 0.78rem;
  color: #777;
  font-style: italic;
}

/* ─── Fullscreen optimizations ─────────────────────────────────── */
/* Improved fullscreen layout */
.gate-scanner-wrap.fullscreen-mode {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100vh;
  max-width: 100%;
  margin: 0;
  padding: 0;
  z-index: 9999;
  background: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.fs-header {
  background: linear-gradient(90deg, #3b5aa7 0%, #c0102a 100%);
  padding: 12px 16px;
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(0,0,0,0.15);
}

/* Scanner card takes remaining space */
.gs-card.fullscreen-card {
  position: relative;
  top: auto;
  left: auto;
  right: auto;
  bottom: auto;
  width: 100%;
  height: auto;
  flex: 1;
  border-radius: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Camera fills available space */
.camera-container.fullscreen-camera {
  position: relative;
  top: auto;
  left: auto;
  right: auto;
  bottom: auto;
  flex: 1;
  width: 100%;
  height: 100%;
  aspect-ratio: auto;
  min-height: auto;
  border-radius: 0;
  border: none;
  margin: 0;
  max-height: 70vh;
}

/* Result card stays on bottom with proper layering */
.gate-scanner-wrap.fullscreen-mode .gs-result-card {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  max-height: 30vh;
  margin: 0;
  border-radius: 0;
  z-index: 10001;
  overflow-y: auto;
  box-shadow: 0 -2px 8px rgba(0,0,0,0.15);
  border: none;
}

/* Larger, more readable text in fullscreen */
.gate-scanner-wrap.fullscreen-mode .gs-result-icon {
  font-size: 3rem;
}

.gate-scanner-wrap.fullscreen-mode .gs-result-title {
  font-size: 1.4rem;
  font-weight: 800;
}

.gate-scanner-wrap.fullscreen-mode .gs-result-name {
  font-size: 1.25rem;
}

.gate-scanner-wrap.fullscreen-mode .gs-result-meta {
  font-size: 0.95rem;
}

.gate-scanner-wrap.fullscreen-mode .gs-result-ticket {
  font-size: 0.9rem;
}

/* Mobile friendliness */
@media (max-width: 768px) {
  .gate-scanner-wrap {
    padding: 3px 0;
  }

  .gs-card {
    border-radius: 6px;
    margin: 6px;
  }

  .gs-card-header {
    padding: 8px 12px;
    font-size: 0.75rem;
  }

  .gs-card-body {
    padding: 12px;
  }

  .camera-container {
    min-height: 200px;
  }

  .gate-scanner-wrap.fullscreen-mode .camera-container.fullscreen-camera {
    bottom: 30vh;
  }

  .gate-scanner-wrap.fullscreen-mode .gs-result-card {
    max-height: 35vh;
  }
}
</style>
